import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button, Modal } from "react-bootstrap";
import { ButtonLoader } from "Components/ButtonLoader";
import { MultiCheckDropdown } from "Components/MultiCheckDropdown";
import { newAuthService } from "Service/Axios";
import { createAdminGroupUrl, updateAdminGroupUrl } from "Constant/api";
import {
  useRoleList,
  useAttributeList,
  useFolderList,
  useEmailTemplateList,
  useEmailWorkflowList,
  useUserList,
} from "Constant/apiHooks";
import { Validator } from "Utils/validator";

export const CreateGroup = ({ show, toggle, selectedData, fetchList }) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);

  const { roleList } = useRoleList();
  const { attributeList } = useAttributeList();
  const { folderList } = useFolderList();
  const { allEmailTemplateList } = useEmailTemplateList();
  const { allEmailWorkflowList } = useEmailWorkflowList();
  const { userList } = useUserList();

  const [formData, setFormData] = useState({
    name: "",
    groupId: "",
    description: "",
    users: [],
    roles: [],
    attributes: [],
    folderIds: [],
    emailTemplateIds: [],
    emailRuleIds: [],
  });
  const [errMsg, setErrMsg] = useState({
    name: "",
    groupId: "",
    description: "",
    users: "",
  });

  const {
    name,
    groupId,
    description,
    users,
    roles,
    attributes,
    folderIds,
    emailTemplateIds,
    emailRuleIds,
  } = formData;

  useEffect(() => {
    if (selectedData) {
      setFormData({
        name: selectedData.name || "",
        groupId: selectedData.groupId || "",
        description: selectedData.description || "",
        users: (selectedData.users || []).map((u) => ({
          label: u.fullName || `${u.firstName} ${u.lastName}`,
          value: u.userId,
        })),
        roles: (selectedData.roles || []).map((r) => ({
          label: r.name,
          value: r.id,
        })),
        attributes: (selectedData.attributes || []).map((a) => ({
          label: a.name,
          value: a.id,
        })),
        folderIds: (selectedData.formfolders || []).map((f) => ({
          label: f.name,
          value: f.id,
        })),
        emailTemplateIds: (selectedData.emailtemplates || []).map((e) => ({
          label: e.name,
          value: e.id,
        })),
        emailRuleIds: (selectedData.emailrules || []).map((e) => ({
          label: e.name,
          value: e.id,
        })),
      });
    }
  }, [selectedData]);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

    if (e.target.name === "name") {
      if (!Validator.text(e.target.value)) {
        setErrMsg((prev) => ({
          ...prev,
          name: t("common.messages.error.adminSettings.groupName.blank"),
        }));
      } else {
        setErrMsg((prev) => ({ ...prev, name: "" }));
      }
    }

    if (e.target.name === "groupId") {
      if (!Validator.text(e.target.value)) {
        setErrMsg((prev) => ({
          ...prev,
          groupId: t("common.messages.error.adminSettings.groupId.blank"),
        }));
      } else {
        setErrMsg((prev) => ({ ...prev, groupId: "" }));
      }
    }

    if (e.target.name === "description") {
      if (!Validator.text(e.target.value)) {
        setErrMsg((prev) => ({
          ...prev,
          description: t(
            "common.messages.error.adminSettings.groupDescription.blank"
          ),
        }));
      } else {
        setErrMsg((prev) => ({ ...prev, description: "" }));
      }
    }
  };

  const handleChangeMultiple = (selected, name) => {
    setFormData((prev) => ({ ...prev, [name]: selected }));
    if (name === "users") {
      if (!Validator.hasMultiple(selected)) {
        setErrMsg((prev) => ({
          ...prev,
          users: t("common.messages.error.adminSettings.userIds.blank"),
        }));
      } else {
        setErrMsg((prev) => ({ ...prev, users: "" }));
      }
    }
  };

  const getValidation = () => {
    return new Promise((resolve) => {
      let isValid = true;
      const errors = {};

      if (!Validator.text(name)) {
        errors["name"] = t(
          "common.messages.error.adminSettings.groupName.blank"
        );
        isValid = false;
      }

      if (!Validator.text(groupId)) {
        errors["groupId"] = t(
          "common.messages.error.adminSettings.groupId.blank"
        );
        isValid = false;
      }

      if (!Validator.text(description)) {
        errors["description"] = t(
          "common.messages.error.adminSettings.groupDescription.blank"
        );
        isValid = false;
      }

      if (!Validator.hasMultiple(users)) {
        errors["users"] = t(
          "common.messages.error.adminSettings.userIds.blank"
        );
        isValid = false;
      }

      setErrMsg((prev) => ({
        ...prev,
        ...errors,
      }));

      resolve(isValid);
    });
  };

  const onSave = async () => {
    const validationStatus = await getValidation();
    if (!validationStatus) {
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name,
        description,
        groupId,
        users: users.map((u) => u.value),
        roles: roles.map((r) => r.value),
        attributes: attributes.map((a) => a.value),
        formfolders: folderIds.map((f) => f.value),
        emailtemplates: emailTemplateIds.map((e) => e.value),
        emailrules: emailRuleIds.map((e) => e.value),
      };
      if (selectedData) {
        payload.gid = selectedData.gid;
        const response = await newAuthService(
          updateAdminGroupUrl,
          "POST",
          payload
        );
        if (response.status) {
          toast.success(t("common.messages.notification.success.updateGroup"));
        } else {
          toast.error(response.message);
        }
      } else {
        const response = await newAuthService(
          createAdminGroupUrl,
          "POST",
          payload
        );
        if (response.status) {
          toast.success(t("common.messages.notification.success.createGroup"));
        } else {
          toast.error(response.message);
        }
      }
      setLoading(false);
      fetchList();
      toggle();
    } catch (err) {
      setLoading(false);
      toast.error(
        (err && err.message) ||
          t("common.messages.notification.failed.somethingWentWrong") ||
          "Failed to save"
      );
    }
  };

  return (
    <Modal
      show={show}
      onHide={toggle}
      size="lg"
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header className="justify-content-center">
        <Modal.Title>{t("auth.adminSettings.createGroup")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row">
          <div className="col-md-6 col-sm-12 mb-3">
            <label className="form-label">
              {t("common.label.name")} <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              name="name"
              placeholder={t("common.placeHolder.name")}
              value={name}
              onChange={handleChange}
            />
            {errMsg.name && <p className="error">{errMsg.name}</p>}
          </div>

          <div className="col-md-6 col-sm-12  mb-3">
            <label className="form-label">
              {t("common.label.groupId")} <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              name="groupId"
              placeholder={t("common.placeHolder.groupId")}
              value={groupId}
              onChange={handleChange}
            />
            {errMsg.groupId && <p className="error">{errMsg.groupId}</p>}
          </div>
        </div>

        <div className="row">
          <div className="col-12 mb-3">
            <label className="form-label">
              {t("common.label.description")}{" "}
              <span className="text-danger">*</span>
            </label>
            <textarea
              className="form-control"
              name="description"
              placeholder={t("common.placeHolder.description")}
              rows={3}
              value={description}
              onChange={handleChange}
            />
            {errMsg.description && (
              <p className="error">{errMsg.description}</p>
            )}
          </div>
        </div>

        <div className="row">
          <div className="col-md-6 col-sm-12 mb-3">
            <label className="form-label">
              {t("common.label.users")} <span className="text-danger">*</span>
            </label>
            <MultiCheckDropdown
              placeholder={t("common.placeHolder.searchUsers")}
              options={(userList || []).map((u) => ({
                label: u.fullName || u.email,
                value: u.userId,
              }))}
              value={users}
              onChange={(selected) => handleChangeMultiple(selected, "users")}
            />
            {errMsg.users && <p className="error">{errMsg.users}</p>}
          </div>

          <div className="col-md-6 col-sm-12 mb-3">
            <label className="form-label">{t("common.label.roles")}</label>
            <MultiCheckDropdown
              placeholder={t("common.placeHolder.searchRoles")}
              options={(roleList || []).map((r) => ({
                label: r.name,
                value: r.gid,
              }))}
              value={roles}
              onChange={(selected) => handleChangeMultiple(selected, "roles")}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-md-6 col-sm-12 mb-3">
            <label className="form-label">{t("common.label.attributes")}</label>
            <MultiCheckDropdown
              placeholder={t("common.placeHolder.searchAttributes")}
              options={(attributeList || []).map((a) => ({
                label: a.name,
                value: a.gid,
              }))}
              value={attributes}
              onChange={(selected) =>
                handleChangeMultiple(selected, "attributes")
              }
            />
          </div>

          <div className="col-md-6 col-sm-12 mb-3">
            <label className="form-label">{t("common.label.formFolder")}</label>
            <MultiCheckDropdown
              placeholder={t("common.placeHolder.searchFormFolder")}
              options={(folderList || []).map((f) => ({
                label: f.name,
                value: f.gid,
              }))}
              value={folderIds}
              onChange={(selected) =>
                handleChangeMultiple(selected, "folderIds")
              }
            />
          </div>
        </div>

        <div className="row">
          <div className="col-md-6 col-sm-12 mb-3">
            <label className="form-label">
              {t("common.label.emailTemplate")}
            </label>
            <MultiCheckDropdown
              placeholder={t("common.placeHolder.searchEmailTemplate")}
              options={(allEmailTemplateList || []).map((e) => ({
                label: e.name || e.title,
                value: e.gid,
              }))}
              value={emailTemplateIds}
              onChange={(selected) =>
                handleChangeMultiple(selected, "emailTemplateIds")
              }
            />
          </div>

          <div className="col-md-6 col-sm-12 mb-3">
            <label className="form-label">{t("common.label.emailRules")}</label>
            <MultiCheckDropdown
              placeholder={t("common.placeHolder.searchEmailRules")}
              options={(allEmailWorkflowList || []).map((e) => ({
                label: e.name,
                value: e.gid,
              }))}
              value={emailRuleIds}
              onChange={(selected) =>
                handleChangeMultiple(selected, "emailRuleIds")
              }
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={toggle}>
          {t("common.button.close")}
        </Button>
        {!loading ? (
          <Button variant="primary" onClick={onSave}>
            {t("common.button.save")}
          </Button>
        ) : (
          <ButtonLoader className="btn-loading-sm" />
        )}
      </Modal.Footer>
    </Modal>
  );
};
